import React from 'react';
import PropTypes from 'prop-types';
import {
  arrayOf,
  customType,
  fragment,
  params,
  shape,
  string,
  typename,
  bool as boolType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { CapabilitiesTileRenderer } from './CapabilitiesTileRenderer';
import { normalizeCapabilitiesTileData } from '../transformData';

const { string: stringType, number, bool, node } = PropTypes;

const ImpressionProviderWrapper = ({
  children = null,
  shouldWrapWithImpressionProvider = false,
  entryId = null
}) => {
  if (shouldWrapWithImpressionProvider) {
    return (
      <ImpressionProvider
        data={{
          id: entryId || '',
          name: 'CapabilitiesTile',
          component: 'CapabilitiesTile',
          type: 'content',
        }}
      >
        {children}
      </ImpressionProvider>
    );
  }
  return children;
};
ImpressionProviderWrapper.propTypes = {
  children: node,
  shouldWrapWithImpressionProvider: bool,
  entryId: stringType
};

export function CapabilitiesTile({
  componentId = '',
  componentClass = '',
  componentPosition = 0,
  id = undefined,
  isB2C = false,
  slotContentId: slotName = '',
  mediaCollectionIndex = 0,
  shouldWrapWithImpressionProvider = false
}) {
  const { data: componentData } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass
    },
    skip: !!slotName || !componentId || !componentClass
  });

  const { data: slotData } = useDataModel('slot', {
    fetchPolicy: 'cache-only',
    variables: { id: slotName },
    skip: !slotName
  });

  const { normalizedData, entryId } = normalizeCapabilitiesTileData({
    slotData,
    componentData,
    mediaCollectionIndex,
    componentId
  });

  if (!normalizedData) return null;

  if (slotName && normalizedData && normalizedData.length > 1) {

    return (
      <ImpressionProviderWrapper
        shouldWrapWithImpressionProvider={shouldWrapWithImpressionProvider}
        entryId={entryId}
      >
        <div className="sui-grid sui-grid-cols-12 sui-gap-4 md:sui-gap-6 xl:sui-gap-8 md:sui-grid-cols-4">
          {normalizedData.map((tile, tileIndex) => {
            return (
              <CapabilitiesTileRenderer
                componentId={tile?.entryId || componentId}
                id={tileIndex}
                isB2C={isB2C}
                componentClass={componentClass}
                componentPosition={tileIndex}
                dataComponent={tile}
              />
            );
          })}
        </div>
      </ImpressionProviderWrapper>
    );

  }

  return (
    <ImpressionProviderWrapper
      shouldWrapWithImpressionProvider={shouldWrapWithImpressionProvider}
      entryId={entryId}
    >
      <CapabilitiesTileRenderer
        componentId={normalizedData[0]?.entryId || componentId}
        id={id}
        isB2C={isB2C}
        componentClass={componentClass}
        componentPosition={componentPosition}
        dataComponent={normalizedData[0]}
      />
    </ImpressionProviderWrapper>
  );
}

CapabilitiesTile.propTypes = {
  componentId: stringType,
  isB2C: bool,
  componentClass: stringType,
  id: stringType,
  componentPosition: number,
  slotContentId: stringType, // slotName for fusion pattern
  mediaCollectionIndex: number,
  shouldWrapWithImpressionProvider: bool
};

CapabilitiesTile.displayName = 'CapabilitiesTile';

CapabilitiesTile.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass').enum(['CapabilitiesTile']).isRequired(),
  }).shape({
    CapabilitiesTile: fragment().shape({
      link: string(),
      id: string(),
      componentName: string(),
      title: string(),
      description: string(),
      cta: string(),
      previewImage: shape({
        damContentSelector: shape({
          assetData: arrayOf(shape({
            selectedImageUrl: string()
          }))
        }),
        damDownloadedContent: shape({
          url: string()
        }),
        id: string(),
      }),
    }),
  }),
  slot: params({
    id: string().isRequired(),
  }).shape({
    id: string(),
    entryId: string(),
    mediaCollections: arrayOf(shape({
      entryId: string(),
      media: arrayOf(shape({
        entryId: string(),
        title: string(),
        callToAction: string(),
        description: string(),
        sourceDesktop: arrayOf(shape({
          entryId: string(),
          uri: string(),
          hyperlink: string(),
        })),
        sourceMobile: arrayOf(shape({
          entryId: string(),
          uri: string(),
          hyperlink: string(),
        })),
      }))
    })),
    __typename: typename('newSlot'),
  })
};
